import { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Calendar, ChevronDown, ChevronRight, Play, ExternalLink, Search, Filter, FileDown, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { InputVenture } from "@/components/ui/input-venture";
import { useClients } from "@/hooks/useClients";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PageTour } from "@/components/tour/PageTour";
import { adminDemosSteps } from "@/lib/tour-steps";

export default function AdminDemosDownloads() {
  const { demos, clients } = useClients();
  const [demosOpen, setDemosOpen] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<string>("all");
  const [dateRange, setDateRange] = useState<string>("all");

  const getClientName = (clientId: string) => {
    return clients.find(c => c.id === clientId)?.name || 'Unknown Client';
  };

  const filteredDemos = useMemo(() => {
    const now = new Date();

    const getDateRangeBounds = (): { start: Date | null; end: Date | null } => {
      if (dateRange === 'all') return { start: null, end: null };

      const end = new Date(now);
      end.setHours(23, 59, 59, 999);

      if (dateRange === 'week') {
        const start = new Date(now);
        start.setDate(now.getDate() - now.getDay()); // start of current week (Sunday)
        start.setHours(0, 0, 0, 0);
        return { start, end };
      }
      if (dateRange === 'month') {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start, end };
      }
      if (dateRange === 'lastMonth') {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        lastEnd.setHours(23, 59, 59, 999);
        return { start, end: lastEnd };
      }
      if (dateRange === '3months') {
        const start = new Date(now);
        start.setMonth(now.getMonth() - 3);
        start.setHours(0, 0, 0, 0);
        return { start, end };
      }
      return { start: null, end: null };
    };

    const { start, end } = getDateRangeBounds();

    return demos.filter(d => {
      const matchesClient = selectedClient === 'all' || d.clientId === selectedClient;
      const matchesSearch =
        d.companyName.toLowerCase().includes(search.toLowerCase()) ||
        d.contactName.toLowerCase().includes(search.toLowerCase()) ||
        (d.clientName || getClientName(d.clientId)).toLowerCase().includes(search.toLowerCase());

      let matchesDate = true;
      if (start && end && d.dateTime) {
        const demoDate = new Date(d.dateTime);
        matchesDate = demoDate >= start && demoDate <= end;
      }

      return matchesClient && matchesSearch && matchesDate;
    });
  }, [demos, selectedClient, search, clients, dateRange]);

  const clientStats = useMemo(() => {
    return clients.map(client => {
      const clientDemos = demos.filter(d => d.clientId === client.id);
      const demosShowed = clientDemos.filter(d => d.status === 'completed').length;
      const demosClosed = clientDemos.filter(d => d.outcome === 'closed').length;
      const showRate = clientDemos.length > 0 ? Math.round(demosShowed / clientDemos.length * 100) : 0;
      const closeRate = demosShowed > 0 ? Math.round(demosClosed / demosShowed * 100) : 0;

      return {
        id: client.id,
        name: client.name,
        package: client.package,
        demos: clientDemos.length,
        showRate,
        closeRate,
      };
    });
  }, [clients, demos]);

  const completedDemos = filteredDemos.filter(d => d.status === 'completed').length;
  const closedDemos = filteredDemos.filter(d => d.outcome === 'closed').length;

  const handleExportCSV = useCallback(() => {
    if (filteredDemos.length === 0) {
      toast.info("No demos to export");
      return;
    }
    const headers = ["Client", "Company", "Contact", "Date", "Closer", "Status", "Outcome", "Deal Value"];
    const rows = filteredDemos.map(d => [
      d.clientName || getClientName(d.clientId),
      d.companyName,
      d.contactName,
      new Date(d.dateTime).toLocaleString(),
      d.closer,
      d.status,
      d.outcome || "pending",
      d.dealValue ? `$${d.dealValue}` : "",
    ]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `demos-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredDemos.length} demos to CSV`);
  }, [filteredDemos, clients]);

  const handleViewDetails = useCallback((demo: typeof filteredDemos[0]) => {
    toast.info(`${demo.companyName} - ${demo.contactName}`, {
      description: `Status: ${demo.status} | Outcome: ${demo.outcome || 'pending'} | Closer: ${demo.closer}${demo.dealValue ? ` | Deal: $${demo.dealValue.toLocaleString()}` : ''}`,
      duration: 5000,
    });
  }, []);

  const getStatusVariant = (status: string): 'success' | 'info' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'completed': return 'success';
      case 'scheduled': return 'info';
      case 'no_show': return 'error';
      default: return 'warning';
    }
  };

  const getOutcomeVariant = (outcome?: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (outcome) {
      case 'closed': return 'success';
      case 'lost': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={adminDemosSteps} tourId="admin-demos" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Demos</h1>
          <p className="text-muted-foreground">Manage all demos across clients</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportCSV}>
          <FileDown className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <Select value={selectedClient} onValueChange={setSelectedClient}>
          <SelectTrigger className="w-[200px]" data-tour-id="admin-demos-client-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Clients" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map(client => (
              <SelectItem key={client.id} value={client.id}>
                {client.name} ({client.package})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[160px]">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="lastMonth">Last Month</SelectItem>
            <SelectItem value="3months">Last 3 Months</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1 min-w-[200px]" data-tour-id="admin-demos-search">
          <InputVenture
            icon={<Search className="w-4 h-4" />}
            placeholder="Search demos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Client Stats Breakdown */}
      {selectedClient === 'all' && clientStats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clientStats.map(client => (
            <VentureCard
              key={client.id}
              className="cursor-pointer hover:border-secondary transition-colors"
              onClick={() => setSelectedClient(client.id)}
            >
              <VentureCardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{client.name}</span>
                  <BadgeVenture variant={client.package === 'elite' ? 'warning' : 'info'} className="text-xs">
                    {client.package}
                  </BadgeVenture>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Demos:</span>
                    <span className="ml-2 font-medium">{client.demos}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Show:</span>
                    <span className={`ml-2 font-medium ${client.showRate >= 75 ? 'text-success' : client.showRate >= 50 ? 'text-warning' : 'text-destructive'}`}>
                      {client.showRate}%
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Close:</span>
                    <span className={`ml-2 font-medium ${client.closeRate >= 30 ? 'text-success' : client.closeRate >= 20 ? 'text-warning' : 'text-destructive'}`}>
                      {client.closeRate}%
                    </span>
                  </div>
                </div>
              </VentureCardContent>
            </VentureCard>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <VentureCard>
          <VentureCardContent className="text-center">
            <p className="text-3xl font-bold">{filteredDemos.length}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-sm text-muted-foreground">Total Demos</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>All demos across all clients in the selected period</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="text-center">
            <p className="text-3xl font-bold text-secondary">{completedDemos}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-sm text-muted-foreground">Completed</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Demos where the prospect showed up and the meeting took place</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="text-center">
            <p className="text-3xl font-bold text-success">{closedDemos}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-sm text-muted-foreground">Closed</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Demos that resulted in a signed deal or sale</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Demos Table */}
      <VentureCard data-tour-id="admin-demos-table">
        <Collapsible open={demosOpen} onOpenChange={setDemosOpen}>
          <CollapsibleTrigger asChild>
            <VentureCardHeader className="cursor-pointer hover:bg-surface transition-colors">
              <div className="flex items-center gap-3">
                {demosOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                <Calendar className="w-5 h-5 text-secondary" />
                <VentureCardTitle>All Demos ({filteredDemos.length})</VentureCardTitle>
              </div>
            </VentureCardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <VentureCardContent>
              {filteredDemos.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No demos recorded yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Prospect/Company</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date & Time</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Closer</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Outcome</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Deal Value</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDemos.map((demo) => (
                        <tr key={demo.id} className="border-b border-border hover:bg-surface transition-colors">
                          <td className="py-3 px-4">
                            <span className="font-medium text-secondary">
                              {demo.clientName || getClientName(demo.clientId)}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium">{demo.companyName}</p>
                              <p className="text-sm text-muted-foreground">{demo.contactName}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-muted-foreground">
                            {new Date(demo.dateTime).toLocaleString()}
                          </td>
                          <td className="py-3 px-4">{demo.closer}</td>
                          <td className="py-3 px-4">
                            <BadgeVenture variant={getStatusVariant(demo.status)}>
                              {demo.status}
                            </BadgeVenture>
                          </td>
                          <td className="py-3 px-4">
                            {demo.outcome ? (
                              <BadgeVenture variant={getOutcomeVariant(demo.outcome)}>
                                {demo.outcome}
                              </BadgeVenture>
                            ) : (
                              <span className="text-muted-foreground text-sm">Pending</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {demo.dealValue ? (
                              <span className="font-medium text-success">${demo.dealValue.toLocaleString()}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                title={demo.recordingUrl ? "Watch Recording" : "No recording available"}
                                disabled={!demo.recordingUrl}
                                className={!demo.recordingUrl ? "opacity-40 cursor-not-allowed" : ""}
                                onClick={() => {
                                  if (demo.recordingUrl) {
                                    window.open(demo.recordingUrl, '_blank');
                                  }
                                }}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                title="View Details"
                                onClick={() => handleViewDetails(demo)}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </VentureCardContent>
          </CollapsibleContent>
        </Collapsible>
      </VentureCard>
    </motion.div>
  );
}
